import { useMutation } from '@apollo/client';
import { Button } from '@mui/material';
import { Field, Formik } from 'formik';
import { TextField } from 'formik-mui';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import { BaseDialog } from '../../../base/components/BaseDialog';
import { Flex } from '../../../base/components/Flex';
import FormikCheckBox from '../../../base/components/form/FormikCheckBox';
import FormikColorPicker from '../../../base/components/form/FormikColorPicker';
import FormikFileSelect from '../../../base/components/form/FormikFileSelect';
import FormikForm from '../../../base/components/form/FormikForm';
import { QrSceneColors } from '../../../client/pages/LaunchCompanyQrPage/components/QrSceneCard';
import {
  ApplicationSceneFragmentFragment,
  EditApplicationSceneDocument,
} from '../../../graphql/graphql-operations';
import useMutationSnackbar from '../../../hooks/useMutationSnackbar';
import { useTranslationPrefix } from '../../../hooks/useTranslationPrefix';

interface Props {
  open: boolean;
  scene: ApplicationSceneFragmentFragment;
  applicationVersionId: string;
  onClose: () => void;
}

interface FormData {
  enabled: boolean;
  name_t: string;
  description: string;
  qrCardColor: string;
  qrLengthMinutes: string;

  file?: File;
  qrIcon?: File;
}

const ApplicationSceneEditDialog = ({
  open,
  scene,
  onClose,
  applicationVersionId,
}: Props) => {
  const { t, _t } = useTranslationPrefix('applications.scene.editDialog');
  const editSceneHandler = useMutationSnackbar('editApplicationScene', {
    translationKey: 'applications.scene.editApplicationScene',
    onSuccessfullyCompleted: () => onClose(),
  });

  const [editMutation] = useMutation(EditApplicationSceneDocument, {
    ...editSceneHandler,
  });

  const handleSubmit = async ({
    file,
    qrIcon,
    qrLengthMinutes,
    ...rest
  }: FormData) => {
    await editMutation({
      variables: {
        scene: {
          applicationSceneId: scene?.id,
          applicationVersionId: applicationVersionId,
          qrLengthMinutes: qrLengthMinutes
            ? Number(qrLengthMinutes)
            : undefined,
          ...rest,
        },
        image: file,
        qrIcon: qrIcon,
      },
    });
  };
  return (
    <BaseDialog
      open={open}
      title={t('applications.scene.editDialog.title', 'Edit scene')}
      onClose={onClose}
    >
      <Formik<FormData>
        initialValues={{
          enabled: !!scene?.enabled,
          name_t: scene.name_t,
          description: scene.description,
          qrCardColor: scene.qrCardColor || '',
          qrLengthMinutes: scene.qrLengthMinutes
            ? `${scene.qrLengthMinutes}`
            : '',
        }}
        validationSchema={getValidationSchema(_t)}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, setFieldValue, values }) => (
          <FormikForm>
            <FormikFileSelect
              name="file"
              label={t(
                'applications.scene.editDialog.previewImage',
                'Preview image',
              )}
              accept="image/*"
              disabled={isSubmitting}
              color="primary"
            />
            <Field
              component={TextField}
              name="name_t"
              variant="outlined"
              label={t('applications.scene.editDialog.name', 'Name')}
            />
            <Field
              component={TextField}
              name="description"
              variant="outlined"
              label={t(
                'applications.scene.editDialog.description',
                'Description',
              )}
              InputProps={{ minRows: 4, maxRows: 10, multiline: true }}
            />

            <FormikFileSelect
              name="qrIcon"
              label={t(
                'applications.scene.editDialog.qrCardIcon',
                'Icon on QR card',
              )}
              accept="image/*"
              disabled={isSubmitting}
              color="primary"
            />

            <FormikColorPicker
              colors={QrSceneColors}
              name="qrCardColor"
              label={t(
                'applications.scene.editDialog.qrCardColor',
                'QR Card color',
              )}
            />

            <Field
              component={TextField}
              name="qrLengthMinutes"
              variant="outlined"
              label={t(
                'applications.scene.editDialog.qrLengthMinutes',
                'Duration minutes',
              )}
            />

            <FormikCheckBox
              name="enabled"
              label={t('applications.scene.editDialog.enabled', 'Enabled')}
            />
            <Flex justifyContent="space-between">
              <Button
                id="application-scene-edit-dialog-cancel"
                size="large"
                color="primary"
                onClick={onClose}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                id="application-scene-edit-dialog-confirm"
                type="submit"
                size="large"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
              >
                {t('applications.scene.editDialog.save', 'Save')}
              </Button>
            </Flex>
          </FormikForm>
        )}
      </Formik>
    </BaseDialog>
  );
};

export default ApplicationSceneEditDialog;

const getValidationSchema = (t: TFunction) =>
  Yup.object({
    qrCardColor: Yup.string().matches(
      /^#[a-fA-F0-9]{6}$/,
      t('form.qrCardColor.notAColor', 'Must be a color as hex #XXXXXX'),
    ),
    qrLengthMinutes: Yup.number()
      .typeError(t('form.qrLengthMinutes.notANumber', 'Must be a whole number'))
      .integer(t('form.qrLengthMinutes.notANumber', 'Must be a whole number'))
      .positive(t('form.qrLengthMinutes.notANumber', 'Must be a whole number')),
  });
